
import { StyleValue } from '@/shared/app/model/StyleValue';
import Template from '@/shared/app/model/templating/Template';
import BaseDish from '@shared/dish/ui/Dish.vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import CarbonscoreDisplay from './CarbonscoreDisplay.vue';
import OriginsScroller from './OriginsScroller.vue';

@Component({
  components: {
    CarbonscoreDisplay,
    OriginsScroller,
  },
})
export default class Dish extends BaseDish {
  originsDone = false;

  done = false;

  doneTimeout = 0;

  mounted() {
    // Première initialisation du Done
    this.$nextTick().then(() => {
      this.initializeDone();
    });
  }

  beforeDestroy() {
    clearTimeout(this.doneTimeout);
  }

  get formattedAllergens() {
    if (!this.allergens) return '';
    if (this.allergensArray.length === 1) return this.allergensArray[0];
    const last = this.allergensArray.pop();
    return `${this.allergensArray.join(', ')} et ${last}`;
  }

  get allergensArray() {
    if (!this.allergens) return [];
    return this.allergens.toString().split(',');
  }

  get containerStyle(): StyleValue {
    const style: StyleValue = {
      backgroundColor: this.config.backgroundColor?.toString(),
      borderRadius: this.config.borderRadius?.toString(),
      padding: this.config.padding?.toString() || `${this.sizeFactor * 2.4}rem`,
    };
    if (this.config.textColor) style.color = this.config.textColor.toString();
    return style;
  }

  get upperStyle(): StyleValue {
    return {
      flexGrow: this.centerContentInUpper ? 1 : 0,
      justifyContent: this.centerContentInUpper ? 'center' : 'flex-start',
    };
  }

  get spacerStyle(): StyleValue {
    return {
      flexGrow: this.centerContentInUpper ? 0 : 1,
    };
  }

  get nameStyle(): StyleValue {
    let multiplier = 10;
    if (this.nameEnglish) multiplier *= 0.85;
    if (this.name.length > 38) multiplier *= 0.9;
    const style: StyleValue = {
      fontSize:
        this.config.nameFontSize?.toString() ||
        `${this.sizeFactor * multiplier}rem`,
    };
    if (this.config.nameTextColor)
      style.color = this.config.nameTextColor?.toString();
    return style;
  }

  get content() {
    let baseContent = this.name;
    baseContent = baseContent.replace(/,, /g, "<br/>");
    return baseContent;
  }

  get nameEnglishStyle(): StyleValue {
    const multiplier = 6.5;
    const style: StyleValue = {
      fontSize:
        this.config.nameFontSize?.toString() ||
        `${this.sizeFactor * multiplier}rem`,
    };
    if (this.config.nameEnglishTextColor)
      style.color = this.config.nameEnglishTextColor?.toString();
    return style;
  }

  get ingredientsStyle() {
    const fontSize = this.rootFontsize * 3.95 * this.sizeFactor;
    return {
      'font-size': `${fontSize}px`,
    };
  }

  get allergensStyle(): StyleValue {
    let multiplier = 2.4;
    if (this.formattedAllergens.length > 40) {
      multiplier = 2;
    }
    const style: StyleValue = {
      fontSize:
        this.config.allergensFontSize?.toString() ||
        `${this.sizeFactor * multiplier * 2}rem`,
      marginBottom: `${this.sizeFactor * 2.4}rem`,
    };
    if (this.config.allergensTextColor)
      style.color = this.config.allergensTextColor.toString();
    return style;
  }

  get priceStyle(): StyleValue {
    let fontSizeFactorRem = 4.8 * this.sizeFactor * this.lowerSizeFactor;
    fontSizeFactorRem *= Number(this.config.priceFontSizeFactor) || 1;

    const priceStyle: StyleValue = {
      fontSize:
        this.config.priceFontSize?.toString() || `${fontSizeFactorRem}rem`,
      height: `${this.sizeFactor * this.lowerSizeFactor * 10}rem`,
      width: `${this.sizeFactor * this.lowerSizeFactor * 18}rem`,
      borderRadius:
        this.config.priceBorderRadius?.toString() ||
        `${this.sizeFactor * 1}rem`,
      backgroundColor: this.config.priceBackgroundColor?.toString() || 'black',
    };

    if (this.config.priceNoBackground) {
      priceStyle.backgroundColor = 'transparent';
      fontSizeFactorRem *= 1.7;
    }

    // Pas d'origines = +4%
    if (!this.origins) fontSizeFactorRem *= 1.04;

    if (this.config.priceTextColor)
      priceStyle.color = this.config.priceTextColor?.toString();
    priceStyle.fontSize =
      this.config.priceFontSize?.toString() || `${fontSizeFactorRem}rem`;

    if (this.price === '')
      priceStyle.width = '0';

    return priceStyle;
  }

  get price2Style(): StyleValue {
    let fontSizeFactorRem = 4.8 * this.sizeFactor * this.lowerSizeFactor;
    fontSizeFactorRem *= Number(this.config.priceFontSizeFactor) || 1;

    const priceStyle: StyleValue = {
      fontSize:
        this.config.priceFontSize?.toString() || `${fontSizeFactorRem}rem`,
      height: `${this.sizeFactor * this.lowerSizeFactor * 10}rem`,
      width: `${this.sizeFactor * this.lowerSizeFactor * 18}rem`,
      borderRadius:
        this.config.priceBorderRadius?.toString() ||
        `${this.sizeFactor * 1}rem`,
      backgroundColor: this.config.priceBackgroundColor?.toString() || 'black',
    };

    if (this.config.priceNoBackground) {
      priceStyle.backgroundColor = 'transparent';
      fontSizeFactorRem *= 1.7;
    }

    // Pas d'origines = +4%
    if (!this.origins) fontSizeFactorRem *= 1.04;

    if (this.config.priceTextColor)
      priceStyle.color = this.config.priceTextColor?.toString();
    priceStyle.fontSize =
      this.config.priceFontSize?.toString() || `${fontSizeFactorRem}rem`;

    if (this.price === '')
      priceStyle.width = '0';

    return priceStyle;
  }

  get originsStyle(): StyleValue {
    const style: StyleValue = {
      borderRadius:
        this.config.priceBorderRadius?.toString() ||
        `${this.sizeFactor * 1}rem`,
    };
    if (this.price !== '') style.marginLeft = `${this.sizeFactor * 2.4}rem`;
    if (this.config.originsBackgroundColor) style.backgroundColor = this.config.originsBackgroundColor.toString();
    if (this.config.originsTextColor) style.color = this.config.originsTextColor.toString();
    return style;
  }

  get originsTemplate(): Template {
    return {
      config: {
        sizeFactor: this.sizeFactor.toString(),
        origins: this.origins,
      },
      datasets: {
        icons: this.dataset('origins'),
      },
    };
  }

  get carbonscoreTemplate(): Template {
    return {
      config: {
        sizeFactor: this.sizeFactor.toString(),
        carbonscore: this.carbonscore,
      },
      datasets: {
        carbonscore: this.dataset('carbonscore'),
      },
    };
  }

  get lowerSizeFactor() {
    return Number(this.config.lowerSizeFactor) || 1;
  }

  get centerContentInUpper() {
    return Boolean(this.config.centerContentInUpper) || false;
  }

  get isPeriph() {
    return Boolean(this.config.isPeriph) || false;
  }

  get showAllergens() {
    return this.config.hideAllergens !== true;
  }

  @Watch('doneDuration')
  @Watch('origins')
  initializeDone() {
    this.done = false;
    this.originsDone = false;
    clearTimeout(this.doneTimeout);
    setTimeout(() => {
      this.done = true;
    }, this.doneDuration);
  }

  get doneDuration() {
    return Math.max(
      this.name.length * 150,
      this.allergens.split(',').length * 500,
    );
  }

  callDone(template?: Template) {
    if (template === this.originsTemplate) {
      this.originsDone = true;
    }
  }

  @Watch('done')
  @Watch('originsDone')
  doneTrigger() {
    if (this.done && this.originsDone) {
      this.emitDone();
    }
  }
}
